// src/storage.ts
import fs from "fs";
import os from "os";
import path from "path";
function getRootDir() {
  return path.join(os.homedir(), ".adhdev", "terminal-mux");
}
var WINDOW_MARKER = "--w--";
function getWorkspacesDir() {
  return path.join(getRootDir(), "workspaces");
}
function getStatePath() {
  return path.join(getRootDir(), "state.json");
}
function sanitizeWorkspaceName(name) {
  return name.trim().replace(/[^a-zA-Z0-9._-]+/g, "-");
}
function toWorkspaceRef(name) {
  const workspaceName = sanitizeWorkspaceName(name);
  const markerIndex = workspaceName.indexOf(WINDOW_MARKER);
  if (markerIndex < 0) {
    return {
      sessionName: workspaceName,
      windowName: workspaceName,
      workspaceName
    };
  }
  return {
    sessionName: workspaceName.slice(0, markerIndex),
    windowName: workspaceName.slice(markerIndex + WINDOW_MARKER.length),
    workspaceName
  };
}
function buildWorkspaceName(sessionName, windowName) {
  const session = sanitizeWorkspaceName(sessionName);
  const window = sanitizeWorkspaceName(windowName);
  if (!session || session === window) return window;
  return `${session}${WINDOW_MARKER}${window}`;
}
function getWorkspaceControlEndpoint(name) {
  const sanitized = sanitizeWorkspaceName(name);
  if (process.platform === "win32") {
    return {
      kind: "pipe",
      path: `\\\\.\\pipe\\adhmux-${sanitized}`
    };
  }
  return {
    kind: "unix",
    path: path.join(os.tmpdir(), `adhmux-${sanitized}.sock`)
  };
}
var TerminalMuxStorage = class {
  workspacesDir = getWorkspacesDir();
  statePath = getStatePath();
  listWorkspaceNames() {
    return this.listWorkspaces().map((workspace) => workspace.name);
  }
  listWorkspaces() {
    if (!fs.existsSync(this.workspacesDir)) return [];
    return fs.readdirSync(this.workspacesDir, { withFileTypes: true }).filter((entry) => entry.isFile() && entry.name.endsWith(".json")).flatMap((entry) => {
      const name = entry.name.replace(/\.json$/, "");
      const filePath = this.getWorkspacePath(name);
      let title = name;
      let paneCount = 0;
      let updatedAt = 0;
      try {
        const parsed = JSON.parse(fs.readFileSync(filePath, "utf8"));
        title = parsed.title || name;
        paneCount = Object.keys(parsed.panes || {}).length;
        updatedAt = fs.statSync(filePath).mtimeMs;
      } catch {
        return [];
      }
      return [{
        name,
        title,
        paneCount,
        updatedAt
      }];
    }).sort((a, b) => b.updatedAt - a.updatedAt || a.name.localeCompare(b.name));
  }
  listSessions() {
    const grouped = /* @__PURE__ */ new Map();
    for (const workspace of this.listWorkspaces()) {
      const ref = toWorkspaceRef(workspace.name);
      const items = grouped.get(ref.sessionName) || [];
      items.push(workspace);
      grouped.set(ref.sessionName, items);
    }
    return Array.from(grouped.entries()).map(([sessionName, workspaces]) => {
      const sorted = [...workspaces].sort((a, b) => b.updatedAt - a.updatedAt || a.name.localeCompare(b.name));
      const active = sorted[0];
      return {
        name: sessionName,
        title: toWorkspaceRef(active.name).sessionName,
        windowCount: workspaces.length,
        updatedAt: active.updatedAt,
        activeWindowName: toWorkspaceRef(active.name).windowName
      };
    }).sort((a, b) => b.updatedAt - a.updatedAt || a.name.localeCompare(b.name));
  }
  listSessionWindows(sessionName) {
    const normalizedSession = sanitizeWorkspaceName(sessionName);
    return this.listWorkspaces().filter((workspace) => toWorkspaceRef(workspace.name).sessionName === normalizedSession);
  }
  resolveSessionWindowWorkspace(sessionName, windowName) {
    const windows = this.listSessionWindows(sessionName);
    if (windows.length === 0) return null;
    if (!windowName) return windows[0].name;
    const normalizedWindow = sanitizeWorkspaceName(windowName);
    const exact = windows.find((workspace) => toWorkspaceRef(workspace.name).windowName === normalizedWindow);
    return exact?.name || null;
  }
  loadWorkspace(name) {
    const filePath = this.getWorkspacePath(name);
    if (!fs.existsSync(filePath)) return null;
    const parsed = JSON.parse(fs.readFileSync(filePath, "utf8"));
    return parsed?.workspaceId ? parsed : null;
  }
  saveWorkspace(name, workspace) {
    fs.mkdirSync(this.workspacesDir, { recursive: true });
    fs.writeFileSync(this.getWorkspacePath(name), JSON.stringify(workspace, null, 2), "utf8");
  }
  renameWorkspace(fromName, toName) {
    const fromPath = this.getWorkspacePath(fromName);
    const toPath = this.getWorkspacePath(toName);
    if (!fs.existsSync(fromPath)) {
      throw new Error(`Workspace not found: ${fromName}`);
    }
    fs.mkdirSync(this.workspacesDir, { recursive: true });
    fs.renameSync(fromPath, toPath);
  }
  deleteWorkspace(name) {
    const filePath = this.getWorkspacePath(name);
    if (!fs.existsSync(filePath)) {
      throw new Error(`Workspace not found: ${name}`);
    }
    fs.unlinkSync(filePath);
    const state = this.loadClientState();
    if (state.lastWorkspace === sanitizeWorkspaceName(name)) {
      this.saveClientState({ ...state, lastWorkspace: void 0, updatedAt: Date.now() });
    }
  }
  getLastWorkspace() {
    const state = this.loadClientState();
    return state.lastWorkspace || null;
  }
  setLastWorkspace(name) {
    this.saveClientState({
      ...this.loadClientState(),
      lastWorkspace: sanitizeWorkspaceName(name),
      updatedAt: Date.now()
    });
  }
  getWorkspacePath(name) {
    return path.join(this.workspacesDir, `${sanitizeWorkspaceName(name)}.json`);
  }
  loadClientState() {
    if (!fs.existsSync(this.statePath)) {
      return { updatedAt: 0 };
    }
    try {
      const parsed = JSON.parse(fs.readFileSync(this.statePath, "utf8"));
      return typeof parsed === "object" && parsed ? parsed : { updatedAt: 0 };
    } catch {
      return { updatedAt: 0 };
    }
  }
  saveClientState(state) {
    fs.mkdirSync(getRootDir(), { recursive: true });
    fs.writeFileSync(this.statePath, JSON.stringify(state, null, 2), "utf8");
  }
};

export {
  getRootDir,
  sanitizeWorkspaceName,
  toWorkspaceRef,
  buildWorkspaceName,
  getWorkspaceControlEndpoint,
  TerminalMuxStorage
};
